const ELEMENT_NODE = 1
const TEXT_NODE = 3

// These are the block elements that can be produced by the editor itself or
// pasted into it. Required line breaks mirror the relevant innerText rule:
// adjacent block breaks collapse, while paragraphs retain a blank line.
const BLOCK_BREAK_COUNTS = new Map([
  ['ADDRESS', 1],
  ['ARTICLE', 1],
  ['ASIDE', 1],
  ['BLOCKQUOTE', 1],
  ['DD', 1],
  ['DIV', 1],
  ['DL', 1],
  ['DT', 1],
  ['FIGCAPTION', 1],
  ['FIGURE', 1],
  ['FOOTER', 1],
  ['H1', 1],
  ['H2', 1],
  ['H3', 1],
  ['H4', 1],
  ['H5', 1],
  ['H6', 1],
  ['HEADER', 1],
  ['LI', 1],
  ['MAIN', 1],
  ['NAV', 1],
  ['OL', 1],
  ['P', 2],
  ['PRE', 1],
  ['SECTION', 1],
  ['TR', 1],
  ['UL', 1]
])

const SKIPPED_ELEMENTS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE'])

const clampOffset = (value, maximum) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(0, Math.min(maximum, Math.trunc(number)))
}

const textToken = value => ({ type: 'text', value })
const requiredBreakToken = count => ({ type: 'requiredBreak', count })
const markerToken = () => ({ type: 'marker' })

const appendTextNode = (tokens, node, boundary) => {
  const value = String(node.nodeValue || '')
  if (boundary.container !== node) {
    if (value) tokens.push(textToken(value))
    return
  }

  const offset = clampOffset(boundary.offset, value.length)
  if (offset > 0) tokens.push(textToken(value.slice(0, offset)))
  tokens.push(markerToken())
  if (offset < value.length) tokens.push(textToken(value.slice(offset)))
}

const appendChildren = (tokens, parent, boundary) => {
  const children = Array.from(parent.childNodes || [])
  const boundaryOffset = boundary.container === parent
    ? clampOffset(boundary.offset, children.length)
    : -1

  children.forEach((child, index) => {
    if (boundaryOffset === index) tokens.push(markerToken())
    appendNode(tokens, child, boundary)
  })
  if (boundaryOffset === children.length) tokens.push(markerToken())
}

const appendNode = (tokens, node, boundary) => {
  if (node.nodeType === TEXT_NODE) {
    appendTextNode(tokens, node, boundary)
    return
  }
  if (node.nodeType !== ELEMENT_NODE) return

  const tagName = String(node.nodeName || '').toUpperCase()
  if (SKIPPED_ELEMENTS.has(tagName)) return
  if (tagName === 'BR') {
    tokens.push(textToken('\n'))
    return
  }

  const breakCount = BLOCK_BREAK_COUNTS.get(tagName) || 0
  if (breakCount) tokens.push(requiredBreakToken(breakCount))
  appendChildren(tokens, node, boundary)
  if (breakCount) tokens.push(requiredBreakToken(breakCount))
}

const concreteTokensAfter = tokens => {
  const result = new Array(tokens.length).fill(false)
  let hasConcrete = false
  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    result[index] = hasConcrete
    const token = tokens[index]
    if (token.type === 'text' && token.value) hasConcrete = true
  }
  return result
}

const normalizeTokens = tokens => {
  const futureConcrete = concreteTokensAfter(tokens)
  const output = []
  let outputLength = 0
  let pendingRequiredBreaks = 0
  let cursorOffset = null

  const append = value => {
    if (!value) return
    output.push(value)
    outputLength += value.length
  }

  tokens.forEach((token, index) => {
    if (token.type === 'requiredBreak') {
      pendingRequiredBreaks = Math.max(
        pendingRequiredBreaks,
        token.count
      )
      return
    }

    if (token.type === 'marker') {
      cursorOffset = outputLength
      if (outputLength > 0 &&
          pendingRequiredBreaks > 0 &&
          futureConcrete[index]) {
        cursorOffset += pendingRequiredBreaks
      }
      return
    }

    if (token.type !== 'text' || !token.value) return
    if (pendingRequiredBreaks > 0) {
      if (outputLength > 0) append('\n'.repeat(pendingRequiredBreaks))
      pendingRequiredBreaks = 0
    }
    append(token.value)
  })

  return {
    document: output.join(''),
    cursorOffset
  }
}

const boundaryBelongsToRoot = (root, container) => Boolean(
  root &&
  container &&
  (root === container || root.contains(container))
)

// Serializes the contenteditable's visible structural text and a DOM boundary
// in one pass. It never inserts a marker into the live editor, so selection,
// undo history, mutation observers, and input events remain untouched.
export const serializeContentEditableDocumentAtBoundary = (
  root,
  container,
  offset
) => {
  if (!boundaryBelongsToRoot(root, container)) return null

  const tokens = []
  appendChildren(tokens, root, { container, offset })
  const result = normalizeTokens(tokens)
  if (!Number.isSafeInteger(result.cursorOffset)) return null

  const cursorOffset = Math.max(
    0,
    Math.min(result.document.length, result.cursorOffset)
  )
  return {
    document: result.document,
    cursorOffset,
    textBeforeCaret: result.document.slice(0, cursorOffset),
    textAfterCaret: result.document.slice(cursorOffset)
  }
}

export const serializeContentEditableDocument = (
  root,
  selection = window.getSelection()
) => {
  if (!root || !selection || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  return serializeContentEditableDocumentAtBoundary(
    root,
    range.endContainer,
    range.endOffset
  )
}
